import React, { useCallback, useState } from "react";

import * as styles from "./MonthCalendar.module.scss";
import {
  getYears,
  generateRange,
  getMonthFromDate,
  getDateFromMonth,
  getMonth
} from "./utils";
import DateOnlyFormat from "components/DateOnlyFormat";
import {
  useClearRefinements,
  useRefinementList,
  UseRefinementListProps
} from "react-instantsearch";
import clsx from "clsx";

interface Props {
  id: string;
  startMonth: string;
  endMonth: string;
  update: (value: string | undefined) => void;
  items: { label: string; isRefined: boolean; count: number }[];
  variant?: "dark" | "default";
}

const monthsInOneYear = 12;

const MonthCalendar = ({
  id,
  startMonth,
  endMonth,
  items,
  update,
  variant = "default"
}: Props) => {
  const yearsToShow = getYears(startMonth, endMonth);
  const [visibleYear, setVisibleYear] = useState(yearsToShow && yearsToShow[0]);

  if (yearsToShow && (!visibleYear || !yearsToShow.includes(visibleYear)))
    setVisibleYear(yearsToShow[0]);

  const onYearSelect: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setVisibleYear(parseInt(e.target.value));
  };

  const darkVariant = variant == "dark";

  return (
    <div
      className={clsx(styles.container, {
        [styles.darkVariant]: darkVariant,
        [styles.whiteVariant]: !darkVariant
      })}
    >
      <header className={styles.header}>
        {yearsToShow?.map((year) => (
          <div key={year} className={styles.yearContainer}>
            <input
              type="radio"
              name={`${id}--year`}
              value={year}
              id={`${id}--year-${year}`}
              className={styles.yearInput}
              checked={year === visibleYear}
              onChange={onYearSelect}
            />
            <label htmlFor={`${id}--year-${year}`} className={styles.year}>
              {year}
            </label>
          </div>
        ))}
      </header>
      <div className={styles.months}>
        {generateRange(monthsInOneYear).map((monthIndex) => {
          const month = getMonth(visibleYear || 2022, monthIndex);
          const resultsInfo = items.find((i) => i.label == month);
          const isSelected = !!resultsInfo && resultsInfo.isRefined;
          const resultsCounter = resultsInfo?.count || 0;
          const isSelectable = resultsCounter > 0;

          const monthId = `${id}--month-${month}`;
          return (
            <div key={month} className={styles.monthContainer}>
              <input
                type="checkbox"
                name={monthId}
                id={monthId}
                className={styles.monthInput}
                disabled={!isSelectable}
                checked={isSelected}
                value={month}
                onChange={(e) => update(e.target.value)}
              />
              <label htmlFor={monthId} className={styles.month}>
                <DateOnlyFormat
                  value={getDateFromMonth(monthIndex)}
                  format={{
                    month: "short"
                  }}
                />
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

type AlgoliaMonthCalendarProps = {
  minYearMonth: string;
  maxYearMonth: string;
  id: string;
  variant?: "dark" | "default";

  //Algolia
  operator: UseRefinementListProps["operator"];
  attribute: UseRefinementListProps["attribute"];
  limit: UseRefinementListProps["limit"];
};

const AlgoliaMonthCalendar: React.FC<AlgoliaMonthCalendarProps> = ({
  id,
  operator,
  attribute,
  limit,
  variant = "default"
}) => {
  const { refine: reset } = useClearRefinements({
    includedAttributes: [attribute]
  });

  const { items, refine } = useRefinementList({
    attribute,
    operator,
    limit
  });

  const availableDates = items
    .filter((x) => x.label)
    .map((x) => new Date(`${x.label}-01`).valueOf());

  const doUpdate = useCallback(
    (value: string | undefined) => {
      if (value) refine(value);
      else reset();
    },
    [refine, reset]
  );

  const firstDepartureDateInTicks = availableDates.length
    ? Math.min(...availableDates)
    : undefined;
  const lastDepartureDateInTicks = availableDates.length
    ? Math.max(...availableDates)
    : undefined;

  const firstDepartureDate =
    firstDepartureDateInTicks && new Date(firstDepartureDateInTicks);

  const lastDepartureDate =
    lastDepartureDateInTicks && new Date(lastDepartureDateInTicks);

  const startMonth = firstDepartureDate && getMonthFromDate(firstDepartureDate);
  const endMonth = lastDepartureDate && getMonthFromDate(lastDepartureDate);

  return (
    <MonthCalendar
      id={id}
      startMonth={startMonth || ""}
      endMonth={endMonth || ""}
      items={items}
      update={doUpdate}
      variant={variant}
    />
  );
};

export default AlgoliaMonthCalendar;
